import classnames from 'classnames/bind'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslate } from 'react-polyglot'
import { GlobalThemeColors } from '~/@types/colors'
import { GlobalGridPreset } from '~/@types/grid-preset'
import { GlobalTextPreset } from '~/@types/text-preset'

import Input from '~/components/Form/Input'
import BlurredCta from '~/components/UI/BlurredCta'

// import SearchKeys from '~/components/Search/SearchKeys'
import { useStyle } from '~/providers/StyleProvider'

import useDebounce from '~/hooks/useDebounce'
import useLocale from '~/hooks/useLocale'
import useOnKeyDown from '~/hooks/useOnKeyDown'

import { stringIsNotEmpty } from '~/utils/check-empty-string'
import formatSearchTerm from '~/utils/format-search-term'
import { getQueryParams } from '~/utils/get-query-params'

import { SEARCH } from '~/data/dictionary'

import css from './styles.module.scss'

const cx = classnames.bind(css)

export type UrlParamsProps = {
  q?: string
}

export interface SearchInputProps {
  className?: string
  inputClassName?: string
  q?: string
}

function SearchInput({ className, inputClassName }: SearchInputProps) {
  const inputRef = useRef<null | HTMLInputElement>(null)
  const router = useRouter()
  const t = useTranslate()
  const locale = useLocale()
  const [searchTerm, setSearchTerm] = useState('')
  const debouncedSearchTerm = useDebounce(searchTerm, 250)

  const inputStyle = useStyle({
    textPreset: GlobalTextPreset.Title18Primary,
    color: GlobalThemeColors.Shade2,
  })

  const clearSearch = useCallback(() => {
    router.push(
      {
        pathname: router.pathname,
        query: {
          q: '',
        },
      },
      null,
      {
        shallow: true,
        locale,
      },
    )
    inputRef.current.value = ''
  }, [])

  const params = useMemo<UrlParamsProps>(() => getQueryParams(), [router])
  const { q } = params ?? {}

  useEffect(() => {
    if (q && q !== inputRef.current.value) {
      inputRef.current.value = formatSearchTerm(String(q))
      setSearchTerm(q)
    }
  }, [q])

  useEffect(() => {
    if (debouncedSearchTerm === 0) {
      clearSearch()
    } else {
      router.push(
        {
          pathname: router.pathname,
          query: {
            q: debouncedSearchTerm,
          },
        },
        null,
        {
          shallow: true,
          locale,
        },
      )
    }
  }, [debouncedSearchTerm])

  useEffect(() => {
    /**
     * Space in string will be displayed as '+' in a query string param
     * e.g: a user querying 'bague or' will set 'q=bague+or' as url param
     * To avoid the input from being set as "bague+or" on first load (refresh or direct link)
     * Remove plus character on first render
     */
    if (q) {
      inputRef.current.value = formatSearchTerm(String(q))
      setSearchTerm(q)
    }
  }, [])

  useOnKeyDown(27, () => {
    if (stringIsNotEmpty(q)) clearSearch()
  })

  const [, setIsFocused] = useState(null)

  const handleFocus = () => {
    setIsFocused(true)
  }

  const gridStyle = useStyle({ grid: GlobalGridPreset.BASE_GRID })

  return (
    <div className={cx(css.SearchInput, className, gridStyle)}>
      <div className={cx(css.container)}>
        <Input
          className={cx(css.input, inputClassName, inputStyle)}
          inputClassName={cx(css.search, inputStyle)}
          onChange={(e) => setSearchTerm(e.target.value)}
          inputStyle={inputStyle}
          forwardRef={inputRef}
          onFocus={handleFocus}
          autoComplete="off"
          preset="search"
        />
        <BlurredCta
          className={cx(css.clearSearch)}
          aria-label={t(SEARCH.RESET)}
          onClick={() => clearSearch()}>
          {t(SEARCH.RESET)}
        </BlurredCta>
      </div>
    </div>
  )
}

SearchInput.defaultProps = {}

export default SearchInput
